import { client as gqlClient } from '@/api/GraphQLClient';

import { ApolloProvider, gql, useQuery } from '@apollo/client';
import {
  Button,
  Grid,
  styled,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { validate as isValidUUID } from 'uuid';
import { useLocation } from 'react-router-dom';
import MuiTable from '@mui/material/Table';
import MuiTableBody from '@mui/material/TableBody';
import MuiTableCell from '@mui/material/TableCell';
import MuiTableContainer from '@mui/material/TableContainer';
import MuiTableRow from '@mui/material/TableRow';

export const CostDebugPage = () => {
  return (
    <MainContainer>
      <ApolloProvider client={gqlClient}>
        <Page />
      </ApolloProvider>
    </MainContainer>
  );
};

export const Page = () => {
  const [threadId, setThreadId] = useState<string | null>(null);

  // Parse threadId from input
  const [inputThreadId, setInputThreadId] = useState<string>('');
  const onChangeThreadId = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (isValidUUID(value)) {
      window.location.pathname = `/debug/cost/${value}`;
      return;
    }
    const parsedThreadId = parseThreadIdFromPath(value);
    if (parsedThreadId && isValidUUID(parsedThreadId)) {
      window.location.pathname = `/debug/cost/${parsedThreadId}`;
      return;
    }
    setInputThreadId(parsedThreadId ?? value);
  };

  // Parse threadId from URL
  const location = useLocation();
  const pathThreadId = parseThreadIdFromPath(location.pathname);
  useEffect(() => {
    if (pathThreadId && pathThreadId !== threadId) {
      setThreadId(pathThreadId);
      setInputThreadId(pathThreadId);
      location.pathname = `/debug/cost/${pathThreadId}`;
    }
  }, [pathThreadId]);

  // Fetch messages and cost events
  const { loading, data, error } = useQuery(GET_THREAD_MESSAGE_COST_EVENTS, {
    skip: !threadId,
    variables: { threadId },
  });

  // Calculate totals
  const messages: any[] =
    data?.thread?.messages?.edges?.map((e: any) => e.node) || [];
  const totalCost = messages.reduce(
    (acc, message) => acc + calcMsgCost(message),
    0,
  );
  const totalEvents = messages.reduce(
    (acc, message) =>
      acc + message.costReports.length + message.stepCosts.length,
    0,
  );

  return (
    <div>
      {error && <Typography color="error">{error.message}</Typography>}
      <Grid container>
        <Grid item xs={3}>
          <Typography variant="h4">Cost Debug</Typography>
        </Grid>
        <Grid item xs={6}>
          URL or ID of Thread{' '}
          <TextField
            fullWidth
            value={inputThreadId}
            onChange={onChangeThreadId}
          />
        </Grid>
        <Grid item xs={3}>
          {loading && <Typography>Loading...</Typography>}
        </Grid>
      </Grid>
      <MuiTableContainer sx={{ overflow: 'initial' }}>
        <MuiTable stickyHeader sx={{ minWidth: '100%' }} size="small">
          <MuiTableRow>
            <TableRowHeader>
              Message ID (count: {messages.length})
            </TableRowHeader>
            <TableRowHeader>Cost (${totalCost.toFixed(2)})</TableRowHeader>
            <TableRowHeader>Events (count: {totalEvents})</TableRowHeader>
            <TableRowHeader>Message</TableRowHeader>
          </MuiTableRow>
          <MuiTableBody>
            {messages.map((message, index) => (
              <MuiTableRow
                key={message.id}
                sx={{ bgcolor: index % 2 ? '#ddd' : '#eee' }}
              >
                <MuiTableCell>{message.id}</MuiTableCell>
                <MuiTableCell>${calcMsgCost(message).toFixed(2)}</MuiTableCell>
                <MuiTableCell>
                  <Tooltip
                    arrow
                    title={
                      <pre>
                        {JSON.stringify(
                          []
                            .concat(
                              message.costReports.map((event: any) =>
                                mkCostReportEvent(event.dataJSON),
                              ),
                            )
                            .concat(
                              message.stepCosts.map((event: any) =>
                                mkStepCostEvent(event.dataJSON),
                              ),
                            ),
                          null,
                          2,
                        )}
                      </pre>
                    }
                  >
                    <Button>
                      {message.costReports.length + message.stepCosts.length}{' '}
                      Events
                    </Button>
                  </Tooltip>
                </MuiTableCell>
                <MuiTableCell>
                  <OneLiner>{message.text}</OneLiner>
                </MuiTableCell>
              </MuiTableRow>
            ))}
          </MuiTableBody>
        </MuiTable>
      </MuiTableContainer>
    </div>
  );
};

const MainContainer = styled('main')`
  height: 100vh;
  padding: ${({ theme }) => theme.spacing(3)};
  width: calc(100% - 65px);
  background-color: ${({ theme }) => theme.palette.background.default};
`;

const TableRowHeader = styled(MuiTableCell)`
  background-color: ${({ theme }) => theme.color.N1.hex};
  clip-path: inset(0px -15px 0px 0px);
  filter: drop-shadow(5px 0px 4px ${({ theme }) => theme.color.N4.hex});
  font-weight: 700;
  left: 0;
  position: sticky;
  text-wrap: nowrap;
  z-index: 1;
`;

const OneLiner = styled('div')`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const GET_THREAD_MESSAGE_COST_EVENTS = gql`
  fragment CostEventFrag on Event {
    id
    type
    dataJSON
  }

  query GetThreadMessageAndCostEvents($threadId: ID) {
    thread(id: $threadId) {
      id
      messages {
        edges {
          node {
            id
            text
            costReports: events(types: [COST_REPORT]) {
              ...CostEventFrag
            }
            stepCosts: events(types: [STEP_COST]) {
              ...CostEventFrag
            }
          }
        }
      }
    }
  }
`;

const calcMsgCost = (message: any) => {
  const costReportEvents: CostEvent[] = message.costReports
    .filter((event: any) => event.type === 'cost_report')
    .map((event: any) => mkCostReportEvent(event.dataJSON));
  const costReportTotal = costReportEvents.reduce(
    (acc, cost) => acc + cost.dollarCost,
    0,
  );
  const stepCostEvents: CostEvent[] = message.stepCosts
    .filter((event: any) => event.type === 'step_cost')
    .map((event: any) => mkStepCostEvent(event.dataJSON));
  const stepCostTotal = stepCostEvents.reduce(
    (acc, cost) => acc + cost.dollarCost,
    0,
  );
  return costReportTotal + stepCostTotal;
};

type CostEvent = {
  dollarCost: number;
  rawEvent: Record<string, number>;
};

const mkCostReportEvent = (dataJSON: string): CostEvent => {
  const eventData = JSON.parse(dataJSON);
  return {
    dollarCost: eventData.self_cost,
    rawEvent: eventData,
  };
};

const mkStepCostEvent = (dataJSON: string): CostEvent => {
  const eventData = JSON.parse(dataJSON);
  return {
    dollarCost: eventData.dollar_cost,
    rawEvent: eventData,
  };
};

export const parseThreadIdFromPath = (path: string): string | null => {
  const match = path.match(
    /\/\w+\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/,
  );
  return match ? match[1] : null;
};
